import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { Pen2 } from '../../../assets/svgs/icons';
import './styles.css';

/**
 * MarkdownPreview Component
 * Renders markdown content into HTML with syntax highlighting support
 * 
 * @param {string} content - The markdown content to render
 * @param {string} className - Additional CSS classes
 * @param {object} options - Marked.js options (optional)
 * @param {boolean} showDownloadButton - Show button to download as PDF (optional)
 * @param {string} downloadFilename - Custom filename for PDF download (optional)
 */
/**
 * Remove code block wrappers se o conteúdo inteiro estiver envolto em ```markdown
 */
const cleanMarkdownContent = (content) => {
  if (!content) return '';

  const strContent = typeof content === 'string' ? content : (content.markdown || '');
  if (!strContent || typeof strContent.trim !== 'function') return '';

  let cleaned = strContent
    .replace(/\\n/g, '\n')
    .replace(/\r\n?/g, '\n')
    .replace(/\u200B/g, '')
    .trim();
  
  // Remove ```markdown ... ``` wrapper
  const codeBlockRegex = /^```(?:markdown|md)?\s*\n([\s\S]*?)\n```$/;
  const match = cleaned.match(codeBlockRegex);
  
  if (match) {
    // console.log('🧹 Removendo wrapper de code block');
    cleaned = match[1];
  }

  cleaned = cleaned
    .replace(/([^\n])\s+(#{2,6})(?=\s*[A-Za-zÀ-ÿ0-9])/g, '$1\n&nbsp;\n$2')
    .replace(/(^|\n)(#{1,6})([^\s#])/g, '$1$2 $3')
    .replace(/\n{2,}/g, '\n&nbsp;\n');
  
  return cleaned;
};

const normalizeHtmlText = (value = '') =>
  value
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');

const inlineNodeToMarkdown = (node) => {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeHtmlText(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const tagName = node.tagName.toLowerCase();
  const content = Array.from(node.childNodes).map(inlineNodeToMarkdown).join('');

  switch (tagName) {
  case 'strong':
  case 'b':
    return `**${content.trim()}**`;
  case 'em':
  case 'i':
    return `*${content.trim()}*`;
  case 'code':
    return `\`${content.trim()}\``;
  case 'a': {
    const href = node.getAttribute('href');
    return href ? `[${content.trim()}](${href})` : content;
  }
  case 'br':
    return '\n';
  default:
    return content;
  }
};

const listNodeToMarkdown = (listNode, indent = '') => {
  const ordered = listNode.tagName.toLowerCase() === 'ol';

  return Array.from(listNode.children)
    .filter((child) => child.tagName?.toLowerCase() === 'li')
    .map((item, index) => {
      const marker = ordered ? `${index + 1}. ` : '- ';
      const inlineParts = [];
      const nestedLists = [];

      Array.from(item.childNodes).forEach((childNode) => {
        if (
          childNode.nodeType === Node.ELEMENT_NODE &&
          ['ul', 'ol'].includes(childNode.tagName.toLowerCase())
        ) {
          nestedLists.push(childNode);
          return;
        }

        inlineParts.push(
          childNode.nodeType === Node.TEXT_NODE
            ? normalizeHtmlText(childNode.textContent || '')
            : inlineNodeToMarkdown(childNode),
        );
      });

      const line = `${indent}${marker}${inlineParts.join('').replace(/\s+\n/g, '\n').trim()}`.trimEnd();
      const nested = nestedLists
        .map((nestedList) => listNodeToMarkdown(nestedList, `${indent}  `))
        .filter(Boolean)
        .join('\n');

      return nested ? `${line}\n${nested}` : line;
    })
    .join('\n');
};

const blockNodeToMarkdown = (node) => {
  if (!node) return '';

  if (node.nodeType === Node.TEXT_NODE) {
    const text = normalizeHtmlText(node.textContent || '').trim();
    return text ? `${text}\n\n` : '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }

  const tagName = node.tagName.toLowerCase();

  if (/^h[1-6]$/.test(tagName)) {
    const level = Number(tagName.slice(1));
    return `${'#'.repeat(level)} ${Array.from(node.childNodes).map(inlineNodeToMarkdown).join('').trim()}\n\n`;
  }

  if (tagName === 'p') {
    return `${Array.from(node.childNodes).map(inlineNodeToMarkdown).join('').trim()}\n\n`;
  }

  if (tagName === 'ul' || tagName === 'ol') {
    return `${listNodeToMarkdown(node)}\n\n`;
  }

  if (tagName === 'blockquote') {
    const content = Array.from(node.childNodes)
      .map((childNode) => blockNodeToMarkdown(childNode).trim())
      .filter(Boolean)
      .join('\n');

    return `${content
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n')}\n\n`;
  }

  if (tagName === 'pre') {
    const codeContent = node.textContent || '';
    return `\`\`\`\n${codeContent.trim()}\n\`\`\`\n\n`;
  }

  if (tagName === 'hr') {
    return '---\n\n';
  }

  if (tagName === 'div') {
    return Array.from(node.childNodes).map(blockNodeToMarkdown).join('');
  }

  return `${Array.from(node.childNodes).map(inlineNodeToMarkdown).join('').trim()}\n\n`;
};

const htmlToMarkdown = (html = '') => {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  if (!root) return '';

  const markdown = Array.from(root.childNodes).map(blockNodeToMarkdown).join('');
  return cleanMarkdownContent(markdown);
};

const MarkdownPreview = ({ 
  content = '', 
  className = '',
  options = {},
  onSave,
  editSessionId = 0,
  isEditDisabled = false,
  defaultEditable = false,
  autoSaveDelayMs = 650,
}) => {
  const { t } = useTranslation();
  const initialContent = cleanMarkdownContent(content);
  const canEdit = typeof onSave === 'function';
  const canAutoEdit = defaultEditable && canEdit && !isEditDisabled;
  const [isEditing, setIsEditing] = useState(canAutoEdit);
  const [currentContent, setCurrentContent] = useState(initialContent);
  const [editableContent, setEditableContent] = useState(initialContent);
  const lastExternalContentRef = useRef(initialContent);
  const lastSavedContentRef = useRef(initialContent);
  const lastEditSessionIdRef = useRef(editSessionId);
  const editablePreviewRef = useRef(null);
  const editableHtmlRef = useRef('');
  const autoSaveTimeoutRef = useRef(null);

  const defaultOptions = {
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false,
    async: false,
    ...options,
  };

  const cleanedContent = useMemo(() => cleanMarkdownContent(content), [content]);

  useEffect(() => {
    if (canAutoEdit) {
      setIsEditing(true);
      return;
    }

    if (defaultEditable && isEditDisabled) {
      setIsEditing(false);
    }
  }, [canAutoEdit, defaultEditable, isEditDisabled]);

  useEffect(() => {
    if (cleanedContent !== lastExternalContentRef.current) {
      lastExternalContentRef.current = cleanedContent;
      setCurrentContent(cleanedContent);

      if (canAutoEdit && cleanedContent !== lastSavedContentRef.current) {
        lastSavedContentRef.current = cleanedContent;
        setEditableContent(cleanedContent);
        return;
      }

      if (!isEditing) {
        setEditableContent(cleanedContent);
      }
    }
  }, [cleanedContent, isEditing, canAutoEdit]);

  useEffect(
    () => () => {
      clearTimeout(autoSaveTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isEditing && !canAutoEdit) {
      setEditableContent(currentContent);
    }
  }, [isEditing, currentContent, canAutoEdit]);

  useEffect(() => {
    const shouldStartEditing = editSessionId > lastEditSessionIdRef.current;

    if (shouldStartEditing) {
      setIsEditing(true);
    }

    lastEditSessionIdRef.current = editSessionId;
  }, [editSessionId]);

  const parseMarkdownToHtml = (value) => {
    if (!value) return '';

    try {
      marked.setOptions(defaultOptions);
      return marked.parse(value);
    } catch (error) {
      console.error('❌ Erro ao converter markdown:', error);
      return `<p style="color: red;">Erro: ${error.message}</p>`;
    }
  };

  const editableHtml = useMemo(
    () => parseMarkdownToHtml(editableContent),
    [editableContent],
  );

  const handleStartEdit = () => {
    if (isEditDisabled) {
      return;
    }

    setEditableContent(currentContent);
    editableHtmlRef.current = parseMarkdownToHtml(currentContent);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditableContent(currentContent);
    editableHtmlRef.current = '';
    setIsEditing(false);
  };

  const persistEditableContent = (exitEditMode = false) => {
    const currentEditableHtml =
      editablePreviewRef.current?.innerHTML ||
      editableHtmlRef.current ||
      editableHtml;
    const normalizedEditableContent = cleanMarkdownContent(
      htmlToMarkdown(currentEditableHtml),
    );

    if (
      canAutoEdit &&
      normalizedEditableContent === lastSavedContentRef.current
    ) {
      return normalizedEditableContent;
    }

    lastSavedContentRef.current = normalizedEditableContent;

    if (canAutoEdit) {
      if (typeof onSave === 'function') {
        onSave(normalizedEditableContent);
      }
      return normalizedEditableContent;
    }

    setCurrentContent(normalizedEditableContent);
    setEditableContent(normalizedEditableContent);

    if (exitEditMode) {
      setIsEditing(false);
    }

    if (typeof onSave === 'function') {
      onSave(normalizedEditableContent);
    }

    return normalizedEditableContent;
  };

  const handleSaveEdit = () => {
    persistEditableContent(true);
  };

  const handleEditableInput = (event) => {
    editableHtmlRef.current = event.currentTarget.innerHTML;

    if (!canAutoEdit) {
      return;
    }

    clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      persistEditableContent(false);
    }, autoSaveDelayMs);
  };

  useEffect(() => {
    if (
      isEditing &&
      editablePreviewRef.current &&
      editablePreviewRef.current.innerHTML !== editableHtml
    ) {
      editablePreviewRef.current.innerHTML = editableHtml;
      editableHtmlRef.current = editableHtml;
    }
  }, [editableHtml, isEditing]);

  const containerClassName = `markdown-preview-container${isEditing ? ' is-editing' : ''}`;
  const canStartEdit = canEdit && !isEditDisabled && !defaultEditable;
  const showManualEditActions = isEditing && !canAutoEdit;

  if (isEditing) {
    return (
      <div className={containerClassName}>
        {showManualEditActions && (
          <div className="markdown-preview-actions markdown-preview-actions-floating">
            <button
              type="button"
              className="markdown-edit-button markdown-edit-button-secondary"
              onClick={handleCancelEdit}
            >
              {t('chatsocket.markdown_preview.cancel')}
            </button>
            <button
              type="button"
              className="markdown-edit-button"
              onClick={handleSaveEdit}
            >
              {t('chatsocket.markdown_preview.save')}
            </button>
          </div>
        )}

        <div
          ref={editablePreviewRef}
          className={`markdown-preview markdown-preview-editable ${showManualEditActions ? 'markdown-preview-with-floating-edit' : ''} ${className}`}
          contentEditable={!isEditDisabled}
          suppressContentEditableWarning
          spellCheck
          onInput={handleEditableInput}
        />
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {/* {canStartEdit && (
        <div className="markdown-preview-actions markdown-preview-actions-floating">
          <button
            type="button"
            className="markdown-edit-button"
            onClick={handleStartEdit}
            aria-label={t('chatsocket.markdown_preview.edit_aria')}
          >
            <Pen2 color="#9002CD" size="16" />
          </button>
        </div>
      )} */}
      <div className={`markdown-preview ${canEdit ? 'markdown-preview-with-floating-edit' : ''} ${className}`}>
        <div dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(currentContent) }} />
      </div>
    </div>
  );
};

export default MarkdownPreview;
